/**
 * Load More Collection - Manual Button Only
 * Premium E-commerce Solution
 * 
 * Key improvements over infinite scroll:
 * - Footer is always accessible
 * - Better UX control for users
 * - Improved SEO (search engines can crawl pagination)
 * - Lower cognitive load for users
 */

class LoadMoreCollection {
  constructor() {
    this.container = document.querySelector('#infinite-products-container');
    this.loadMoreBtn = document.querySelector('[data-load-more-btn]');
    this.loader = document.querySelector('[data-load-more-loader]');
    this.productCount = document.querySelector('[data-loadmore-count]');
    this.currentPage = 1;
    this.isLoading = false;
    this.totalPages = 1;
    this.totalProducts = 0;
    this.retryCount = 0;
    this.maxRetries = 5;
    
    this.init();
  }

  init() {
    if (!this.container) {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.init(), 200);
        return;
      }
      console.warn('LoadMoreCollection: Container not found');
      return;
    }
    
    this.setupData();
    this.setupEventListeners();
    this.updateProductCount();
    this.disableThemePagination();
    this.setupDomObserver();
  }

  setupDomObserver() {
    // Watch for Pipeline's AJAX filter/sort updates that replace DOM
    const collectionContent = document.querySelector('[data-products-grid]');
    if (!collectionContent) return;

    this.observer = new MutationObserver((mutations) => {
      // Check if our load more button was replaced
      const currentBtn = document.querySelector('[data-load-more-btn]');
      if (currentBtn && currentBtn !== this.loadMoreBtn) {
        // DOM was replaced by filter/sort, reinitialize
        this.reinitialize();
      }
    });

    this.observer.observe(collectionContent, {
      childList: true,
      subtree: true
    });
  }

  reinitialize() {
    // Re-cache all DOM references after AJAX update
    this.container = document.querySelector('#infinite-products-container');
    this.loadMoreBtn = document.querySelector('[data-load-more-btn]');

    // Safety bailout if filter returns zero products (no load-more elements)
    if (!this.container || !this.loadMoreBtn) return;

    this.loader = document.querySelector('[data-load-more-loader]');
    this.productCount = document.querySelector('[data-loadmore-count]');
    this.isLoading = false;

    // Re-read data attributes from new DOM
    this.setupData();

    // Re-attach event listeners to new button
    this.setupEventListeners();

    // Update count display
    this.updateProductCount();

    // Re-hide theme pagination
    this.disableThemePagination();
  }

  setupData() {
    if (this.loadMoreBtn) {
      this.totalPages = parseInt(this.loadMoreBtn.dataset.totalPages) || 1;
      this.currentPage = parseInt(this.loadMoreBtn.dataset.currentPage) || 1;
    }
    
    if (this.container) {
      this.totalProducts = parseInt(this.container.dataset.totalProducts) || 0;
      const containerPage = parseInt(this.container.dataset.currentPage);
      if (containerPage) {
        this.currentPage = containerPage;
      }
    }
  }

  setupEventListeners() {
    if (this.loadMoreBtn) {
      // Remove any existing event listeners by cloning
      const newBtn = this.loadMoreBtn.cloneNode(true);
      this.loadMoreBtn.parentNode.replaceChild(newBtn, this.loadMoreBtn);
      this.loadMoreBtn = newBtn;
      
      this.loadMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.loadMoreProducts();
      });
    }
  }

  disableThemePagination() {
    // Hide any theme pagination elements (but keep SEO pagination)
    const themePagination = document.querySelectorAll('.pagination:not(.seo-pagination)');
    themePagination.forEach(p => p.style.display = 'none');
    
    // Hide any other theme load more buttons
    const themeButtons = document.querySelectorAll('.btn--load-more:not([data-load-more-btn])');
    themeButtons.forEach(btn => {
      btn.style.display = 'none';
      btn.disabled = true;
    });
  }

  async loadMoreProducts() {
    if (this.isLoading || this.currentPage >= this.totalPages) return;

    this.isLoading = true;
    this.showLoader();

    try {
      const nextPage = this.currentPage + 1;
      const url = this.buildNextPageUrl(nextPage);
      
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newProductsContainer = doc.querySelector('#infinite-products-container');
      if (!newProductsContainer) {
        console.warn('LoadMoreCollection: No products container in response');
        this.hideLoadMore();
        return;
      }
      
      const newProducts = newProductsContainer.querySelectorAll('.product-item-wrapper');
      if (newProducts.length === 0) {
        this.hideLoadMore();
        return;
      }

      // Append new products with animation
      newProducts.forEach((product, index) => {
        const clone = product.cloneNode(true);
        clone.style.opacity = '0';
        clone.style.transform = 'translateY(20px)';
        this.container.appendChild(clone);
        
        // Stagger animation for each product
        setTimeout(() => {
          clone.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          clone.style.opacity = '1';
          clone.style.transform = 'translateY(0)';
        }, index * 50);
      });

      this.currentPage = nextPage;
      
      // Update button data attribute
      if (this.loadMoreBtn) {
        this.loadMoreBtn.dataset.currentPage = this.currentPage;
      }

      // Update URL without page reload
      this.updateUrl(nextPage);
      
      // Update product count display
      this.updateProductCount();
      
      // Reinitialize theme features for new products
      this.reinitializeThemeFeatures();

      // Check if we've loaded all pages
      if (this.currentPage >= this.totalPages) {
        this.hideLoadMore();
      }

    } catch (error) {
      console.error('LoadMoreCollection: Error loading products', error);
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoader();
    }
  }

  buildNextPageUrl(page) {
    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('page', page);
      return currentUrl.toString();
    } catch (error) {
      // Fallback for older browsers
      const separator = window.location.href.includes('?') ? '&' : '?';
      return `${window.location.href}${separator}page=${page}`;
    }
  }

  updateUrl(page) {
    try {
      const url = this.buildNextPageUrl(page);
      if (window.history && window.history.replaceState) {
        window.history.replaceState({ page: page }, '', url);
      }
    } catch (error) {
      // Silent fallback - URL update is not critical
    }
  }

    updateProductCount() {
    if (!this.productCount) return;
    
    const loadedProducts = this.container.querySelectorAll('.product-item-wrapper').length;
    // Ensure we never show more than total products
    const displayCount = Math.min(loadedProducts, this.totalProducts);
    this.productCount.textContent = displayCount;
    
    // Update any progress indicator
    const progressEl = document.querySelector('[data-load-progress]');
    if (progressEl) {
        progressEl.textContent = `${displayCount} / ${this.totalProducts}`;
    }
    }

  reinitializeThemeFeatures() {
    const newProductItems = this.container.querySelectorAll('.product-item-wrapper:not([data-theme-initialized])');
    
    newProductItems.forEach((item, index) => {
      item.setAttribute('data-theme-initialized', 'true');
      this.fixDuplicateIds(item, index);
    });
    
    // Pipeline theme specific reinit
    if (window.theme && typeof window.theme.reinitProductGridItem === 'function') {
      try {
        window.theme.reinitProductGridItem();
      } catch (e) {
        console.warn('LoadMoreCollection: Theme reinit failed', e);
      }
    }
    
    // Dispatch custom event for other scripts
    document.dispatchEvent(new CustomEvent('loadmore:products:added'));
    document.dispatchEvent(new CustomEvent('theme:reinit'));
    
    // Reinit lazy loading if available
    if (window.lazySizes) {
      window.lazySizes.autoSizer.checkElems();
    }
  }

  fixDuplicateIds(item, index) {
    const elementsWithIds = item.querySelectorAll('[id]');
    elementsWithIds.forEach(element => {
      const currentId = element.id;
      if (currentId && typeof currentId === 'string' && currentId.trim() !== '') {
        element.id = `${currentId}-loadmore-${this.currentPage}-${index}`;
      }
    });
  }

  showLoader() {
    if (this.loader) this.loader.style.display = 'flex';
    if (this.loadMoreBtn) {
      this.loadMoreBtn.disabled = true;
      this.loadMoreBtn.classList.add('is-loading');
    }
  }

  hideLoader() {
    if (this.loader) this.loader.style.display = 'none';
    if (this.loadMoreBtn && this.currentPage < this.totalPages) {
      this.loadMoreBtn.disabled = false;
      this.loadMoreBtn.classList.remove('is-loading');
    }
  }

  showError() {
    if (this.loadMoreBtn) {
      const errorText = this.loadMoreBtn.dataset.errorText || 'Error - Retry';
      this.loadMoreBtn.textContent = errorText;
      this.loadMoreBtn.disabled = false;
      this.loadMoreBtn.classList.add('has-error');
      this.loadMoreBtn.classList.remove('is-loading');
    }
  }

  hideLoadMore() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = 'none';
    }
    if (this.loader) {
      this.loader.style.display = 'none';
    }
    
    // Show "all products loaded" message
    const controlsWrapper = document.querySelector('.load-more-controls');
    if (controlsWrapper) {
      const completeMessage = document.createElement('p');
      completeMessage.className = 'load-more-complete';
      completeMessage.textContent = controlsWrapper.dataset.completeText || 'All products loaded';
      controlsWrapper.appendChild(completeMessage);
    }
  }

  destroy() {
    // Disconnect observer to prevent memory leaks
    if (this.observer) {
      this.observer.disconnect();
    }
    this.isLoading = false;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.loadMoreCollection = new LoadMoreCollection();
  });
} else {
  window.loadMoreCollection = new LoadMoreCollection();
}

// Handle Shopify theme editor section reload
document.addEventListener('shopify:section:load', (e) => {
  if (e.detail && e.target.querySelector('.collection__products')) {
    if (window.loadMoreCollection) {
      window.loadMoreCollection.destroy();
    }
    setTimeout(() => {
      window.loadMoreCollection = new LoadMoreCollection();
    }, 100);
  }
});

// Export for external use
window.LoadMoreCollection = LoadMoreCollection;
